import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import type { PageContext } from '../../lib/types';
import * as store from '../../store/ui';
import { AuthDesignPreview } from '../auth/AuthDesignPreview';
import { parseHash } from '../../lib/router';
import './design-preview-app.css';

const FlowsPage = lazy(() => import('./FlowsPage').then(module => ({ default: module.FlowsPage })));
const ExecutionsPage = lazy(() => import('./ExecutionsPage').then(module => ({ default: module.ExecutionsPage })));
const SettingsPage = lazy(() => import('./SettingsPage').then(module => ({ default: module.SettingsPage })));
const KnowledgePage = lazy(() => import('./KnowledgePage').then(module => ({ default: module.KnowledgePage })));
const AssetDetailPage = lazy(() => import('./AssetDetailPage').then(module => ({ default: module.AssetDetailPage })));
const CanvasV2DesignPreview = lazy(() => import('./CanvasDesignPreview').then(module => ({ default: module.CanvasV2DesignPreview })));
const CanvasV1DesignPreview = lazy(() => import('./CanvasDesignPreview').then(module => ({ default: module.CanvasV1DesignPreview })));
const FlowAppDesignPreview = lazy(() => import('./FlowAppDesignPreview').then(module => ({ default: module.FlowAppDesignPreview })));

const links = [
  { href: '#/flows', label: 'Flows', group: 'Library' },
  { href: '#/executions', label: 'Executions', group: 'Library' },
  { href: '#/knowledge', label: 'Knowledge', group: 'Resources' },
  { href: '#/canvas-v2', label: 'Canvas v2', group: 'Preview states' },
  { href: '#/canvas-v1', label: 'Canvas v1 node', group: 'Preview states' },
  { href: '#/apps', label: 'Apps list', group: 'Preview states' },
  { href: '#/app/emoji', label: 'Flow App', group: 'Preview states' },
  { href: '#/settings', label: 'Settings', group: 'Account' },
  { href: '#/assets/asset-1', label: 'Asset detail', group: 'Preview states' },
];

export function DesignPreviewApp() {
  const [route, setRoute] = useState(() => parseHash());
  useEffect(() => {
    const update = () => setRoute(parseHash());
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);

  const context = useMemo<PageContext>(() => ({
    path: route.path,
    params: /^\/assets\/([^/]+)$/.test(route.path) ? { assetId: route.path.split('/')[2] } : /^\/app\/([^/]+)$/.test(route.path) ? { appId: route.path.split('/')[2] } : /^\/executions\/([^/]+)$/.test(route.path) ? { runId: route.path.split('/')[2] } : {},
    query: route.query,
    workspaceId: null,
    rerender: () => setRoute(parseHash()),
  }), [route]);

  const content = route.path === '/flows' || route.path === '/' ? <FlowsPage context={context} />
    : route.path === '/executions' || /^\/executions\/[^/]+$/.test(route.path) ? <ExecutionsPage context={context} />
    : route.path === '/settings' ? <SettingsPage context={context} />
    : route.path === '/knowledge' ? <KnowledgePage context={context} />
    : route.path === '/canvas-v2' ? <CanvasV2DesignPreview />
    : route.path === '/canvas-v1' ? <CanvasV1DesignPreview />
    : route.path === '/apps' || /^\/app\/[^/]+$/.test(route.path) ? <FlowAppDesignPreview context={context} />
    : /^\/assets\/[^/]+$/.test(route.path) ? <AssetDetailPage context={context} />
    : route.path === '/auth/signup' || route.path === '/auth/login' ? <AuthDesignPreview path={route.path === '/auth/signup' ? '/signup' : '/login'} />
    : <div className="v2-preview-empty"><h1>Choose a screen to preview</h1><p>This preview uses local examples only. App services and backend requests are disabled.</p></div>;

  const isCanvas = route.path === '/canvas-v1' || route.path === '/canvas-v2';
  const isFlowApp = route.path === '/apps' || /^\/app\/[^/]+$/.test(route.path);
  const isFullSurface = isCanvas || isFlowApp;
  const hidePrimarySidebar = isFullSurface || route.path === '/settings';
  const [shellTheme, setShellTheme] = useState<'dark' | 'light'>(() => new URLSearchParams(location.search).get('theme') === 'dark' || store.get().theme === 'dark' ? 'dark' : 'light');
  const toggleTheme = () => setShellTheme(current => {
    const next = current === 'dark' ? 'light' : 'dark';
    store.set({ theme: next === 'dark' ? 'dark' : 'studio' });
    return next;
  });
  const currentLink = links.find(link => route.path === link.href.slice(1)
    || link.href === '#/executions' && route.path.startsWith('/executions')
    || link.href === '#/assets/asset-1' && route.path.startsWith('/assets/')
    || link.href === '#/app/emoji' && route.path.startsWith('/app/'));
  return <div className="v2-preview-shell" data-theme={shellTheme} data-full-surface={isFullSurface ? 'true' : undefined} data-hide-primary-sidebar={hidePrimarySidebar ? 'true' : undefined}>
    <header className="v2-preview-topbar">
      <a className="v2-preview-logo" href="#/flows" aria-label="Bots & Bones"><img src="/assets/bnb-logo.png" alt="" /></a>
      <a className="v2-preview-workspace" href="#/flows">Bots &amp; Bones <span aria-hidden="true">⌄</span></a>
      <nav className="v2-preview-product-tabs" aria-label="Product"><a href="#/flows" aria-current={!isFlowApp ? 'page' : undefined}>Studio</a><a href="#/apps" aria-current={isFlowApp ? 'page' : undefined}>Apps</a></nav>
      <span className="v2-preview-topbar-spacer" />
      <div className="v2-preview-global-search"><span aria-hidden="true">⌕</span><span>Search projects, flows, assets…</span><kbd>⌘K</kbd></div>
      <div className="v2-preview-account"><span>A</span><small>4,793 bxb</small></div>
      <span className="v2-preview-mode" title="Local fixtures; backend requests are disabled">Preview</span>
    </header>
    <div className="v2-preview-body">
      {!isFullSurface && <aside className="v2-preview-sidebar">
        <nav className="v2-preview-nav-group" aria-label="Welcome"><span>Welcome</span><a className="is-existing" aria-disabled="true" title="Already in the application">Dashboard</a></nav>
        <nav className="v2-preview-nav-group" aria-label="Library"><span>Library</span>
          <a className="is-existing" aria-disabled="true" title="Already in the application">Brands</a>
          <a className="is-existing" aria-disabled="true" title="Already in the application">Assets</a>
          <a className="is-existing" aria-disabled="true" title="Already in the application">Projects</a>
          {links.filter(link => link.group === 'Library').map(link => <a key={link.href} href={link.href} aria-current={currentLink === link ? 'page' : undefined}>{link.label}</a>)}
        </nav>
        <nav className="v2-preview-nav-group" aria-label="Resources"><span>Resources</span>{links.filter(link => link.group === 'Resources').map(link => <a key={link.href} href={link.href} aria-current={currentLink === link ? 'page' : undefined}>{link.label}</a>)}</nav>
        <div className="v2-preview-sidebar-bottom"><button type="button" onClick={toggleTheme} aria-label={`Switch to ${shellTheme === 'dark' ? 'light' : 'dark'} theme`}>◐ <span>Theme</span><small>{shellTheme === 'dark' ? 'Dark' : 'Light'}</small></button><a href="#/knowledge">Help</a><a href="#/settings" aria-current={route.path === '/settings' ? 'page' : undefined}>Settings</a></div>
      </aside>}
      <main className="v2-preview-content"><Suspense fallback={<div className="v2-preview-loading" role="status">Loading preview…</div>}>{content}</Suspense></main>
    </div>
  </div>;
}
